import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#variantMenuController?.abort();
    this.#variantMenuController = null;

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /* ---- variant pill: add another option --------------------------------
     A line's variant pill (cart-products.liquid) opens a list of the
     product's variants; choosing one adds one of it. "+" is untouched. */

  /** @type {AbortController | null} */
  #variantMenuController = null;

  /**
   * Places the list under its pill when it opens, and drops the listeners
   * that close it when it shuts.
   * @param {ToggleEvent} event
   */
  onVariantMenuToggle(event) {
    const menu = event.target;
    if (!(menu instanceof HTMLElement)) return;

    this.#variantMenuController?.abort();
    this.#variantMenuController = null;

    if (event.newState !== 'open') {
      menu.classList.remove('cart-variant__menu--placed');
      return;
    }

    const pill = this.querySelector(`[popovertarget="${menu.id}"]`);
    if (!(pill instanceof HTMLElement)) return;

    this.#placeVariantMenu(menu, pill);

    // Besides a choice, a tap outside (the browser's own light dismiss) and
    // Escape, the list closes when the shopper scrolls the cart or the page.
    // Only a scroll that moves the pill counts: the recommendations carousel
    // and the announcement bar scroll by themselves and are ignored, as is
    // scrolling inside the list. A resize counts only when the width changes,
    // so a phone's address bar showing or hiding leaves the list open.
    const controller = new AbortController();
    this.#variantMenuController = controller;
    /** @param {Event} scrollEvent */
    const onScroll = (scrollEvent) => {
      const scroller = scrollEvent.target;
      if (!(scroller instanceof Node) || !scroller.contains(pill)) return;
      this.#hideVariantMenu(menu);
    };
    const openWidth = window.innerWidth;
    const onResize = () => {
      if (window.innerWidth !== openWidth) this.#hideVariantMenu(menu);
    };
    window.addEventListener('scroll', onScroll, { capture: true, passive: true, signal: controller.signal });
    window.addEventListener('resize', onResize, { passive: true, signal: controller.signal });

    this.#variantOptions(menu)[0]?.focus({ preventScroll: true });
  }

  /**
   * Arrow keys move through the list; Escape closes only the list, not the
   * drawer behind it.
   * @param {KeyboardEvent} event
   */
  onVariantMenuKeydown(event) {
    const menu = event.target;
    if (!(menu instanceof HTMLElement)) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.#hideVariantMenu(menu);
      const pill = this.querySelector(`[popovertarget="${menu.id}"]`);
      if (pill instanceof HTMLElement) pill.focus();
      return;
    }

    if (event.key === 'Tab') {
      this.#hideVariantMenu(menu);
      return;
    }

    const options = this.#variantOptions(menu);
    if (!options.length) return;

    const at = document.activeElement instanceof HTMLButtonElement ? options.indexOf(document.activeElement) : -1;
    /** @type {Record<string, number>} */
    const moves = {
      ArrowDown: Math.min(options.length - 1, at + 1),
      ArrowUp: Math.max(0, at - 1),
      Home: 0,
      End: options.length - 1,
    };
    const next = moves[event.key];
    if (next === undefined) return;

    event.preventDefault();
    options[next]?.focus({ preventScroll: true });
  }

  /**
   * Adds one of the chosen option.
   * @param {MouseEvent} event
   */
  onVariantAdd(event) {
    const option = event.target;
    if (!(option instanceof HTMLButtonElement) || option.disabled) return;

    const menu = option.closest('.cart-variant__menu');
    if (menu instanceof HTMLElement) this.#hideVariantMenu(menu);

    const row = option.closest('tr[data-key]');
    const variantId = option.dataset.variantId;
    if (!(row instanceof HTMLTableRowElement) || !variantId) return;

    this.#addVariant(row, variantId, option.getAttribute('aria-selected') === 'true', option.dataset.announce ?? '');
  }

  /**
   * The variant in the line: its own quantity goes up by one (a change on
   * that line, so whatever the line carries is kept). Another variant: one
   * is added; Shopify merges it into a plain line of that variant if there
   * is one, so the cart never lists one variant twice. Either way the line
   * it landed on is highlighted and brought into view. If it cannot be added
   * (sold out, too few left) the cart is unchanged and the reason shows
   * under the line the shopper started from.
   * @param {HTMLTableRowElement} row
   * @param {string} variantId
   * @param {boolean} isCurrent
   * @param {string} announcement
   */
  async #addVariant(row, variantId, isCurrent, announcement) {
    const line = this.refs.cartItemRows.indexOf(row) + 1;
    const rowKey = row.dataset.key ?? '';
    const pill = row.querySelector('.cart-variant__pill');
    const hadFocus = row.contains(document.activeElement) || document.activeElement === document.body;
    const mode = this.isDrawer ? 'hydration' : 'full';
    if (line < 1 || !rowKey) return;

    const sectionsToUpdate = new Set([this.sectionId]);
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    }
    const sections = Array.from(sectionsToUpdate).join(',');
    const sections_url = window.location.pathname;

    pill?.setAttribute('aria-busy', 'true');
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    try {
      /** @type {any} */
      let cart;
      /** @type {Record<string, string> | undefined} */
      let sectionsHtml;
      let targetKey = rowKey;

      if (isCurrent) {
        const quantityInput = row.querySelector('input[data-cart-line]');
        const current = quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.defaultValue, 10) || 1 : 1;
        const response = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig('json', { body: JSON.stringify({ id: rowKey, quantity: current + 1, sections, sections_url }) })
        );
        cart = await response.json();
        if (!response.ok || cart.errors || cart.status) {
          throw new Error(cart.description || cart.errors || cart.message || 'Failed to update cart');
        }
        sectionsHtml = cart.sections;
      } else {
        const response = await fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections, sections_url }),
          })
        );
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Failed to add to cart');
        }
        sectionsHtml = result.sections;
        targetKey = result.items?.[0]?.key ?? '';
        cart = await this.fetchCartData();
      }

      resetShimmer(this);
      this.#updateQuantitySelectors(cart);

      // Reported as an update of the line it landed on, not an "add": an add
      // auto-opens the cart drawer, which on the cart page would pop it open.
      const targetLine = cart.items?.find((/** @type {{ key: string }} */ item) => item.key === targetKey);
      const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: [{ id: targetKey, quantity: targetLine?.quantity ?? 1 }],
          promise: deferredUpdatePromise.promise,
        })
      );
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: sectionsHtml,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-items-component',
          didError: false,
        },
      });

      const sectionHtml = sectionsHtml?.[this.sectionId];
      const existingKeys = new Set(this.refs.cartItemRows?.map((cartRow) => cartRow.dataset.key) ?? []);
      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml, { mode });
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
      }
      this.#updateCartQuantitySelectorButtonStates();

      const rows = this.refs.cartItemRows ?? [];
      const targetRow = rows.find((cartRow) => cartRow.dataset.key === targetKey);
      if (targetRow && !prefersReducedMotion()) {
        // A brand-new line slides in, as lines added from elsewhere do.
        const newRow = !existingKeys.has(targetKey);
        if (newRow) targetRow.classList.add('adding');
        targetRow.classList.remove('cart-items__table-row--just-added');
        void targetRow.offsetWidth;
        targetRow.classList.add('cart-items__table-row--just-added');
        // A fixed timer rather than onAnimationEnd: that waits on every
        // animation in the row's subtree, and an endless one would hold it.
        setTimeout(() => targetRow.classList.remove('adding', 'cart-items__table-row--just-added'), 1700);
      }
      targetRow?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });

      // Focus returns to the pill the shopper started from.
      if (hadFocus) {
        const startPill = rows
          .find((cartRow) => cartRow.dataset.key === rowKey)
          ?.querySelector('.cart-variant__pill');
        if (startPill instanceof HTMLElement) startPill.focus({ preventScroll: true });
      }

      const status = this.querySelector('[data-cart-variant-status]');
      if (status) status.textContent = announcement;
    } catch (error) {
      console.error(error);
      resetShimmer(this);

      const message = error instanceof Error ? error.message : String(error);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      this.#showLineError(line, message);
      if (hadFocus && pill instanceof HTMLElement) pill.focus({ preventScroll: true });
    } finally {
      pill?.removeAttribute('aria-busy');
      this.#enableCartItems();
    }
  }

  /**
   * Positions the list under the pill (above it when there is no room
   * below), kept inside the drawer.
   * @param {HTMLElement} menu
   * @param {HTMLElement} pill
   */
  #placeVariantMenu(menu, pill) {
    const gutter = 8;
    const pillRect = pill.getBoundingClientRect();
    const bounds = (pill.closest('dialog') ?? document.documentElement).getBoundingClientRect();
    const minLeft = Math.max(gutter, bounds.left + gutter);
    const maxRight = Math.min(window.innerWidth, bounds.right) - gutter;
    const width = Math.min(Math.max(pillRect.width, 200), 260, maxRight - minLeft);
    const isRtl = getComputedStyle(pill).direction === 'rtl';
    const start = isRtl ? pillRect.right - width : pillRect.left;

    menu.style.width = `${width}px`;
    menu.style.left = `${Math.min(Math.max(start, minLeft), maxRight - width)}px`;

    const height = menu.offsetHeight;
    const below = pillRect.bottom + 6;
    const above = pillRect.top - height - 6;
    const flip = below + height > window.innerHeight - gutter && above >= gutter;
    menu.style.top = `${flip ? above : below}px`;
    menu.classList.add('cart-variant__menu--placed');
  }

  /** @param {HTMLElement} menu */
  #hideVariantMenu(menu) {
    if (menu.matches(':popover-open')) menu.hidePopover();
  }

  /**
   * @param {HTMLElement} menu
   * @returns {HTMLButtonElement[]}
   */
  #variantOptions(menu) {
    return Array.from(menu.querySelectorAll('.cart-variant__option:not(:disabled)')).filter(
      (option) => option instanceof HTMLButtonElement
    );
  }

  /**
   * Shows a message in a line's own error slot.
   * @param {number} line
   * @param {string} message
   */
  #showLineError(line, message) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
